import { useEffect, useRef, useCallback } from "react";
import { ReactQRCode } from "@lglab/react-qr-code";

interface BagQRPrintProps {
  show: boolean;
  handleClose: () => void;
  inventoryItemId: string;
  bagNo: string;
  autoPrint?: boolean;
}

const BagQRPrint = ({
  show,
  handleClose,
  inventoryItemId,
  bagNo,
  autoPrint = false,
}: BagQRPrintProps) => {
  const qrRef = useRef<HTMLDivElement>(null);

  const printQR = useCallback(() => {
    if (!qrRef.current) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    // Get the QR SVG element
    const svgElement = qrRef.current.querySelector("svg");
    if (!svgElement) return;

    const serializer = new XMLSerializer();
    let svgString = serializer.serializeToString(svgElement);

    // Remove fixed width/height attributes from the SVG to allow CSS sizing
    svgString = svgString
      .replace(/width="[^"]*"/, "")
      .replace(/height="[^"]*"/, "");

    // Convert cm to pixels (assuming 96 DPI: 1cm = 37.8px)
    const stickerWidthPx = 5 * 37.8; // 5cm
    const stickerHeightPx = 7 * 37.8; // 7cm
    const qrSizePx = 4.5 * 37.8; // 4.5cm

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Bag QR Code</title>
          <style>
            @page {
              size: ${stickerWidthPx}px ${stickerHeightPx}px;
              margin: 0;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              width: ${stickerWidthPx}px;
              height: ${stickerHeightPx}px;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              padding: 5px;
              font-family: Arial, sans-serif;
            }
            .qr-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            }
            svg {
              width: ${qrSizePx}px !important;
              height: ${qrSizePx}px !important;
              display: block;
            }
            .bag-number {
              margin-top: 8px;
              font-size: 14px;
              font-weight: bold;
              text-align: center;
              color: #000;
            }
            @media print {
              body {
                margin: 0;
                padding: 5px;
              }
              .qr-container {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            ${svgString}
            <div class="bag-number">${bagNo}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      // Close window after print dialog is closed
      setTimeout(() => {
        printWindow.close();
        handleClose();
      }, 100);
    }, 250);
  }, [bagNo, handleClose]);

  useEffect(() => {
    if (show && autoPrint) {
      setTimeout(() => {
        printQR();
      }, 300);
    }
  }, [show, autoPrint, printQR]);

  if (!show) return null;

  return (
    <div style={{ display: "none" }}>
      <div ref={qrRef} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <ReactQRCode value={inventoryItemId} />
      </div>
      {!autoPrint && (
        <button onClick={printQR} style={{ display: "none" }}>
          Print
        </button>
      )}
    </div>
  );
};

export default BagQRPrint;

